import type { ReactNode } from 'react';
import { formatMoney } from '@belong/common';
import { Text } from '@belong/ui';
import clsx from 'clsx';
import gradientBorderCss from 'styles/gradient-border.module.css';

type Props = {
  title: string;
  used: number;
  budget: number;
  layout?: 'stacked' | 'inline';
  className?: string;
  titleAddon?: ReactNode;
};

export function BudgetMeter({
  title,
  used,
  budget,
  layout = 'stacked',
  className,
  titleAddon,
}: Props) {
  const isOverBudget = used > budget;
  const usedPercentage =
    budget > 0 ? Math.min((used * 100) / budget, 100) : 100;

  const amount = (
    <span className="whitespace-nowrap">
      {formatMoney(used, 'DOLLARS_NO_CENTS')}
      <span className="text-dark-gray font-regular">
        {' '}
        Of {formatMoney(budget, 'DOLLARS_NO_CENTS')}
      </span>
    </span>
  );

  const meterProps = {
    role: 'meter',
    'aria-label': title,
    'aria-valuemin': 0,
    'aria-valuemax': budget,
    'aria-valuenow': Math.min(used, budget),
    'aria-valuetext': isOverBudget
      ? `${formatMoney(used, 'DOLLARS_NO_CENTS')} of ${formatMoney(budget, 'DOLLARS_NO_CENTS')}, ${formatMoney(used - budget, 'DOLLARS_NO_CENTS')} over budget`
      : `${formatMoney(used, 'DOLLARS_NO_CENTS')} of ${formatMoney(budget, 'DOLLARS_NO_CENTS')}`,
  };

  const trackClassName = clsx(
    'rounded-small w-full overflow-hidden border border-dashed',
    isOverBudget
      ? 'border-orange/60 bg-orange/10'
      : 'border-dark-green/60 bg-green/10',
  );

  if (layout === 'inline') {
    return (
      <div
        {...meterProps}
        className={clsx(
          isOverBudget
            ? trackClassName
            : clsx(
                'rounded-small w-full overflow-hidden border border-solid',
                gradientBorderCss.gradientBorder,
              ),
          'h-xl px-xs gap-xs relative flex items-center justify-between',
          className,
        )}
      >
        <div
          className={clsx(
            'absolute inset-y-0 left-0',
            isOverBudget ? 'bg-orange/40' : 'bg-green/60',
          )}
          style={{ width: `${usedPercentage}%` }}
        />
        <div className="gap-2xs relative flex items-center">
          <Text variant="p1" fontWeight="semibold" className="sm:body">
            {title}
          </Text>
          {titleAddon}
        </div>
        <Text variant="p1" fontWeight="semibold" className="sm:body relative">
          {amount}
        </Text>
      </div>
    );
  }

  const remaining = Math.max(budget - used, 0);
  const caption = isOverBudget
    ? `${formatMoney(used - budget, 'DOLLARS_NO_CENTS')} Over Budget`
    : `${formatMoney(remaining, 'DOLLARS_NO_CENTS')} Remaining`;

  return (
    <div className={clsx('gap-xs flex flex-col', className)}>
      <div className="gap-2xs flex items-center">
        <Text variant="h3" fontWeight="semibold">
          {title}
        </Text>
        {titleAddon}
      </div>
      <div {...meterProps} className={clsx('h-sm', trackClassName)}>
        <div
          className={clsx('h-full', isOverBudget ? 'bg-orange/40' : 'bg-green')}
          style={{ width: `${usedPercentage}%` }}
        />
      </div>
      <div className="gap-xs flex items-baseline justify-between">
        <Text className={isOverBudget ? 'text-orange' : 'text-dark-gray'}>
          {caption}
        </Text>
        <Text fontWeight="semibold">{amount}</Text>
      </div>
    </div>
  );
}
